/* ============================================================== */
/*                                                                */
/*   CardPatientHistoryV4  ·  หน้า Patient History Viewer (4d)     */
/*   "ประวัติการรักษาทุกครั้ง (อ่านอย่างเดียว)"                     */
/*                                                                */
/*   mock mode: ส่ง props.mockMode=true -> ใช้ข้อมูลจำลองของแบบ        */
/*   patient/mockPatientHistoryV4.ts (หน้าตาตรงกับ design 100%)     */
/*   live mode: อ่านจาก state.PatientHistoryV4Sequence ผ่าน          */
/*   props.runSequence({ sequence: "PatientHistoryV4", ... })       */
/*                                                                */
/* ============================================================== */

import React, { useEffect, useMemo, useState, useRef } from "react";

import { AlertV4, ModalV4 } from "../common/ui";
import type { PatientMenuKey } from "./PatientPanelV4";
import {
  ACTIONS as HISTORY_ACTIONS,
  CARD_NAME as HISTORY_CARD_NAME,
  SEQUENCE_NAME as HISTORY_SEQUENCE,
  historyBtnKey,
  readHistoryCertificate,
  readHistoryFormCertificate,
  type HistoryRunSequenceProp,
} from "../common/sequence/PatientHistoryV4";
import {
  formCertificateView,
  HISTORY_PERIODS,
  HistoryEncounterRow,
  HistoryFilter,
  HistoryIds,
  HistoryPeriodKey,
  HistorySection,
  HistoryV4Slice,
  LOCKED_TEXT,
  historyDateError,
  printableHistoryImageHtml,
  safeHistoryUrl,
  type HistoryFileRow,
  historyPartLabel,
  buildHistorySections,
  emptyHistoryV4Slice,
  encounterInfoText,
  encounterLabel,
  filterEncounters,
  hiddenLabel,
  normalizeHistoryV4Slice,
  periodNote,
  printableEmrSummaryHtml,
  sectionNavItems,
  sortEncounters,
  type HistoryDetail,
} from "../patient/historyModelV4";
import {
  MOCK_HISTORY_DIVISION,
  MOCK_HISTORY_NOW,
  MOCK_HISTORY_TODAY_NUM,
  MOCK_HISTORY_VISITS,
  mockHistoryDetail,
  mockHistoryEncounters,
} from "../patient/mockPatientHistoryV4";
import { printHtmlDocument } from "../patient/printEmrV4";

/* ============================================================== */
/*   PROPS                                                         */
/* ============================================================== */

export type CardPatientHistoryV4Props = {
  patient?: { hn?: string; name?: string };
  apiToken?: string;
  /** state.PatientHistoryV4Sequence (null/undefined = live empty) */
  history?: unknown;
  mockMode?: boolean;
  ids?: HistoryIds;
  runSequence?: HistoryRunSequenceProp;
  buttonLoadCheck?: Record<string, any>;
  errorMessage?: Record<string, any>;
  setProp?: (key: string, value: unknown) => void;
  /** แผนกที่ให้กรองได้ (ทั้งหมด + แผนกปัจจุบัน) ตามแบบ 4d */
  divisionOptions?: { id?: number; label: string }[];
  currentDivisionName?: string;
  onSelectMenu?: (menu: PatientMenuKey) => void;
  onBackToQueue?: () => void;
};

/* ============================================================== */
/*   STYLE ตามแบบ 4d (ค่าที่วัดจากหน้า design เรนเดอร์จริง)          */
/* ============================================================== */

/* PatientPanelV4 renders <main class="hv4-main"> already (padding 16/20/24 + overflow-y:auto).
   The card is only the inner column of that main. Adding hv4-main here again was the bug:
   padding counts twice (content 40px narrower than the design) and the card becomes its own
   scroller, so main collapses to ~889px while the design main is 2829px tall. */
const CONTENT_STYLE: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 14,
};

const ARROW_BUTTON = (enabled: boolean): React.CSSProperties => ({
  minHeight: 44,
  minWidth: 44,
  border: "1px solid #CBD3D7",
  borderRadius: 4,
  background: "#fff",
  fontSize: 14,
  color: "#243036",
  opacity: enabled ? 1 : 0.4,
});

const PERIOD_BUTTON = (on: boolean): React.CSSProperties => ({
  minHeight: 44,
  padding: "0 16px",
  borderRadius: 22,
  fontSize: 14,
  border: `1px solid ${on ? "#3FA3BB" : "#CBD3D7"}`,
  background: on ? "#DDF1F6" : "#fff",
  fontWeight: on ? 700 : 400,
  color: "#243036",
});

const TYPE_BUTTON = (on: boolean): React.CSSProperties => ({
  minHeight: 44,
  padding: "0 18px",
  border: 0,
  fontSize: 14,
  fontWeight: 700,
  color: "#fff",
  background: on ? "#46B5E8" : "#A7D8EE",
});

const PLAIN_BUTTON: React.CSSProperties = {
  minHeight: 44,
  padding: "0 14px",
  border: "1px solid #CBD3D7",
  borderRadius: 4,
  background: "#fff",
  fontSize: 14,
};

const FILTER_PANEL: React.CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  overflowX: "auto",
  gap: 8,
  alignItems: "flex-end",
  padding: "10px 12px",
  border: "1px solid #D5DEE2",
  borderRadius: 6,
  background: "#fff",
};

const VISIT_CARD: React.CSSProperties = {
  border: "1px solid #D5DEE2",
  borderTop: "3px solid #3FA3BB",
  borderRadius: 6,
  background: "#fff",
};

const STICKY_HEAD: React.CSSProperties = {
  position: "sticky",
  top: 0,
  zIndex: 2,
  background: "#fff",
  borderBottom: "1px solid #D5DEE2",
  padding: "12px 20px 8px",
  borderRadius: "6px 6px 0 0",
};

const NAV_PILL: React.CSSProperties = {
  minHeight: 30,
  padding: "0 12px",
  border: "1px solid #CBD3D7",
  borderRadius: 15,
  background: "#F3F6F7",
  fontSize: 13,
  color: "#243036",
};

const PRINT_BUTTON: React.CSSProperties = {
  minHeight: 36,
  padding: "0 14px",
  border: 0,
  borderRadius: 4,
  background: "#1B7FCC",
  color: "#fff",
  fontSize: 13,
  fontWeight: 600,
  marginLeft: "auto",
};

const HIDE_BUTTON: React.CSSProperties = {
  minHeight: 36,
  padding: "0 12px",
  border: "1px solid #CBD3D7",
  borderRadius: 4,
  background: "#fff",
  color: "#8A1C12",
  fontSize: 13,
};

const SECTION_HEAD: React.CSSProperties = {
  margin: "0 0 8px",
  padding: "6px 10px",
  background: "#EAF3F6",
  borderLeft: "4px solid #3FA3BB",
  fontSize: 16,
  fontWeight: 700,
};

const ROW_WRAP: React.CSSProperties = {
  border: "1px solid #D5DEE2",
  borderRadius: 4,
  overflow: "hidden",
};

const rowStyle = (grid: string): React.CSSProperties => ({
  display: "grid",
  gridTemplateColumns: grid,
  gap: 12,
  alignItems: "center",
  padding: "9px 14px",
  borderBottom: "1px solid #E3E9EC",
  background: "#fff",
});

const gridStyle = (grid: string): React.CSSProperties => ({
  display: "grid",
  gridTemplateColumns: grid,
  gap: 10,
  padding: "8px 4px",
  borderBottom: "1px solid #E3E9EC",
  fontSize: 14,
});

const tableHeadStyle = (grid: string): React.CSSProperties => ({
  ...gridStyle(grid),
  fontWeight: 700,
  color: "#51646A",
  borderBottom: "2px solid #D5DEE2",
});

const FLAG_CHIP: (flag: string) => React.CSSProperties = (flag) => ({
  display: "inline-block",
  padding: "2px 10px",
  borderRadius: 6,
  fontWeight: 700,
  ...(["HH", "LL"].includes(flag) ? { background: "#8A1C12", color: "#fff" } : {}),
  ...(flag === "H" ? { background: "#FBE0DD", color: "#8A1C12" } : {}),
  ...(flag === "L" ? { background: "#FFF1C9", color: "#5A3E00" } : {}),
});

const msgStyle = (): React.CSSProperties => ({
  marginTop: 10,
  padding: "8px 12px",
  borderRadius: 6,
  background: "#E4F4E8",
  fontSize: 14,
});

const lockedStyle = (): React.CSSProperties => ({
  marginTop: 14,
  padding: 24,
  border: "1px dashed #CBD3D7",
  borderRadius: 6,
  textAlign: "center",
  color: "#5B6C73",
});

/* ============================================================== */
/*   helper                                                          */
/* ============================================================== */

/** "วันนี้"รูปแบบ พ.ศ. (25691030) ใช้เทียบช่วงเวลา 3 เดือน/1 ปี */
const nowBeDateNum = (): number => {
  const now = new Date();
  return (now.getFullYear() + 543) * 10000 + (now.getMonth() + 1) * 100 + now.getDate();
};

const FILTER_INITIAL: HistoryFilter = {
  /* แบบ 4d เปิดด้วย "3 เดือนล่าสุด" */
  period: "3m",
  search: "",
  opd: true,
  ipd: true,
  from: "",
  to: "",
  division: "",
  doctor: "",
};

const errorTextOf = (errorMessage: Record<string, any> | undefined): string | null => {
  const value = errorMessage?.[HISTORY_CARD_NAME];
  if (value === undefined || value === null || value === "") return null;
  return typeof value === "string" ? value : JSON.stringify(value);
};

/* ============================================================== */
/*   CARD                                                            */
/* ============================================================== */

const CardPatientHistoryV4: React.FC<CardPatientHistoryV4Props> = (props) => {
  const live = props.mockMode !== true;
  const slice: HistoryV4Slice = live
    ? normalizeHistoryV4Slice(props.history)
    : emptyHistoryV4Slice();

  /* live mode เริ่มจาก filter ที่ sequence เก็บไว้ (client filter ของแบบ 4d) */
  const [filter, setFilter] = useState<HistoryFilter>(() =>
    live ? { ...FILTER_INITIAL, ...slice.filter } : FILTER_INITIAL,
  );
  const [showFilter, setShowFilter] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  /** encounter ที่ผู้ใช้ซ่อนเฉพาะหน้าจอนี้ (ไม่ใช่ server privacy) */
  const [hiddenIds, setHiddenIds] = useState<number[]>([]);
  const [msg, setMsg] = useState("");
  const certificateRequest = useRef(0);
  const [certificate, setCertificate] = useState<Record<string, any> | null>(null);

  const ids = props.ids ?? slice.ids;
  /* client filter ของแบบ 4d เทียบด้วยตัวเลข พ.ศ. (mock ใช้วันอ้างอิงของแบบ, live ใช้วันจริง) */
  const todayNum = live ? nowBeDateNum() : MOCK_HISTORY_TODAY_NUM;
  const divisionFallback = props.currentDivisionName ?? (live ? "" : MOCK_HISTORY_DIVISION);

  const allEncounters: HistoryEncounterRow[] = live
    ? (ids.patientId !== undefined && slice.ids.patientId !== undefined && ids.patientId !== slice.ids.patientId ? [] : sortEncounters(slice.encounters))
    : mockHistoryEncounters();

  const encounters = useMemo(
    () => filterEncounters(allEncounters, live ? { ...slice.filter, search: filter.search } : filter, todayNum),
    [allEncounters, filter, todayNum, slice.filter, live],
  );

  const current = encounters.find((row) => row.id === selectedId) ?? encounters[0] ?? null;
  const currentIndex = current === null ? -1 : encounters.findIndex((row) => row.id === current.id);

  const isLocked = current !== null && (hiddenIds.includes(current.id) || current.isSecret);
  const displayRow = (row: HistoryEncounterRow): HistoryEncounterRow =>
    hiddenIds.includes(row.id) ? { ...row, isSecret: true } : row;

  useEffect(() => {
    setHiddenIds([]);
    setSelectedId(null);
    setMsg("");
  }, [ids.patientId]);

  useEffect(() => {
    if (selectedId !== null && !encounters.some((row) => row.id === selectedId)) setSelectedId(null);
  }, [encounters, selectedId]);

  useEffect(() => {
    ++certificateRequest.current;
    setCertificate(null);
    if (isLocked && live) {
      props.setProp?.("PatientHistoryV4Sequence.detail", null);
      props.setProp?.("PatientHistoryV4Sequence.detailFor", null);
    }
  }, [ids.patientId, current?.id, isLocked]);

  /* live mode: โหลด list ครั้งแรก + รายละเอียดของ encounter ที่เลือก */
  useEffect(() => {
    if (!live || !props.runSequence) return;
    props.runSequence({
      sequence: HISTORY_SEQUENCE,
      action: HISTORY_ACTIONS.LOAD,
      card: HISTORY_CARD_NAME,
      restart: true,
      ...ids,
      filter,
    });
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [live, ids.patientId]);

  useEffect(() => {
    if (!live || !props.runSequence || current === null || isLocked) return;
    if (slice.detailFor === current.id) return;
    props.runSequence({
      sequence: HISTORY_SEQUENCE,
      action: HISTORY_ACTIONS.LOAD_DETAIL,
      card: HISTORY_CARD_NAME,
      ...ids,
      encounterId: current.id,
      emrId: current.emrId ?? undefined,
    });
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [live, ids.patientId, current?.id, slice.detailFor, isLocked]);

  const detail: HistoryDetail | null = isLocked ? null : live
    ? slice.detailFor === current?.id
      ? slice.detail
      : null
    : current === null
      ? null
      : mockHistoryDetail(MOCK_HISTORY_VISITS[currentIndex < 0 ? 0 : currentIndex]);

  const sections = detail === null ? [] : buildHistorySections(detail);
  const nav = sectionNavItems(sections);

  const loading =
    props.buttonLoadCheck?.[historyBtnKey({ card: HISTORY_CARD_NAME, action: HISTORY_ACTIONS.LOAD })] === "LOADING";

  const error = errorTextOf(props.errorMessage);
  const dismissError = (): void => props.setProp?.(`errorMessage.${HISTORY_CARD_NAME}`, null);

  const partialErrors = live && detail !== null && detail.partialErrors.length > 0;

  /* ---------------------------------------------------------- */
  /*   interaction (ตรงกับปุ่มในแบบ 4d)                          */
  /* ---------------------------------------------------------- */

  const moveEncounter = (step: number): void => {
    if (current === null) return;
    const next = encounters[currentIndex + step];
    if (next === undefined) return;
    setSelectedId(next.id);
    setMsg("");
    if (live && props.runSequence) {
      props.runSequence({
        sequence: HISTORY_SEQUENCE,
        action: HISTORY_ACTIONS.SELECT,
        card: HISTORY_CARD_NAME,
        ...ids,
        encounterId: next.id,
      });
    }
  };

  const pickEncounterByLabel = (label: string): void => {
    const row = encounters.find((item) => encounterLabel(displayRow(item)) === label);
    if (row === undefined) return;
    setSelectedId(row.id);
    setMsg("");
    if (live && props.runSequence) {
      props.runSequence({
        sequence: HISTORY_SEQUENCE,
        action: HISTORY_ACTIONS.SELECT,
        card: HISTORY_CARD_NAME,
        ...ids,
        encounterId: row.id,
      });
    }
  };

  const patchFilter = (patch: Partial<HistoryFilter>): void => {
    setFilter((previous) => ({ ...previous, ...patch }));
  };

  const applySearch = (next: HistoryFilter = filter): void => {
    if (historyDateError(next) || !live || !props.runSequence) return;
    const divisionId = props.divisionOptions?.find((option) => option.label === next.division)?.id;
    props.runSequence({
      sequence: HISTORY_SEQUENCE,
      action: HISTORY_ACTIONS.LOAD,
      card: HISTORY_CARD_NAME,
      ...ids,
      divisionId,
      filter: next,
    });
  };

  const selectPeriod = (period: HistoryPeriodKey): void => {
    /* the invalid date blocks the reload: show why instead of highlighting a period that was not applied */
    if (live && historyDateError(filter)) {
      setShowFilter(true);
      return;
    }
    const next = { ...filter, period };
    setFilter(next);
    applySearch(next);
  };

  const clearFilter = (): void => {
    const cleared: HistoryFilter = { ...FILTER_INITIAL, period: filter.period };
    setFilter(cleared);
    setMsg("");
    applySearch(cleared);
  };

  const toggleHide = (): void => {
    if (current === null) return;
    if (current.isSecret) {
      setMsg("ประวัตินี้ถูกปกปิดโดยระบบ ไม่สามารถยกเลิกจากหน้านี้ได้");
      return;
    }
    const hidden = hiddenIds.includes(current.id);
    if (!hidden && live) {
      props.setProp?.("PatientHistoryV4Sequence.detail", null);
      props.setProp?.("PatientHistoryV4Sequence.detailFor", null);
      props.runSequence?.({ sequence: HISTORY_SEQUENCE, action: HISTORY_ACTIONS.SELECT,
        card: HISTORY_CARD_NAME, ...ids, encounterId: current.id });
    }
    setHiddenIds((previous) =>
      hidden ? previous.filter((id) => id !== current.id) : [...previous, current.id],
    );
    setMsg(hidden ? "แสดงประวัติในหน้าจอนี้แล้ว" : "ซ่อนในหน้าจอนี้แล้ว ยังไม่ได้บันทึกการปกปิดในระบบ");
  };

  const printEmr = (): void => {
    if (current === null || isLocked) {
      setMsg("ไม่สามารถพิมพ์ได้: ประวัติ Encounter นี้ถูกปกปิด");
      return;
    }
    if (live && (detail === null || slice.detailLoading || loading)) return;
    if (partialErrors && !window.confirm("ข้อมูลบางส่วนโหลดไม่สำเร็จ ต้องการพิมพ์ข้อมูลที่มีอยู่หรือไม่?")) return;
    const html = printableEmrSummaryHtml({
      patientName: props.patient?.name || current.patientName || "",
      hn: props.patient?.hn || current.hn,
      encounter: current,
      sections,
      printedAt: live ? new Date().toLocaleString("en-GB") : MOCK_HISTORY_NOW,
    });
    const ok = printHtmlDocument(html, {
      onError: () => setMsg("เปิดพิมพ์ไม่สำเร็จ: เครื่องพิมพ์ไม่พร้อม"),
      onMissingImages: (count) => setMsg(`เปิดหน้าต่างพิมพ์แล้ว แต่โหลดรูปไม่สำเร็จ ${count} รูป (เอกสารแสดงข้อความแทนรูป)`),
    });
    setMsg(
      ok
        ? `เปิดหน้าต่างพิมพ์ EMR Summary แล้ว (${current.dateText.slice(0, 10)}) — เลือก "บันทึกเป็น PDF" ที่ปลายทางการพิมพ์`
        : "เปิดหน้าต่างพิมพ์ไม่ได้ในหน้านี้ ลองเปิดหน้าเต็มแล้วกดพิมพ์อีกครั้ง",
    );
  };

  const printFile = async (file: HistoryFileRow): Promise<void> => {
    if (isLocked) return;
    if (file.certificate) {
      if (!live) { setMsg("ข้อมูลใบรับรองจำลอง ไม่มีไฟล์ต้นฉบับ"); return; }
      const token = ++certificateRequest.current;
      const [payload, error] = file.formCertificate
        ? await readHistoryFormCertificate(file.id, props.apiToken)
        : await readHistoryCertificate(file.id, props.apiToken);
      if (token !== certificateRequest.current) return;
      if (error || !payload) { setMsg("โหลดใบรับรองแพทย์ไม่สำเร็จ"); return; }
      setCertificate({
        ...(file.formCertificate ? formCertificateView(payload) : payload),
        historyPatientId: ids.patientId,
        historyEncounterId: current?.id,
      });
      return;
    }
    if (!safeHistoryUrl(file.url)) { setMsg("ไม่มีไฟล์ต้นฉบับสำหรับเอกสารนี้"); return; }
    const ok = printHtmlDocument(printableHistoryImageHtml(file), {
      requireImages: true,
      onError: () => setMsg("เปิดพิมพ์ไม่สำเร็จ: โหลดรูปต้นฉบับไม่ได้"),
    });
    setMsg(ok ? `เปิดหน้าต่างพิมพ์ ${file.name} แล้ว` : "เปิดหน้าต่างพิมพ์ไม่ได้ในหน้านี้");
  };

  const scrollToSection = (id: string): void => {
    const node = document.getElementById(id);
    if (node && typeof node.scrollIntoView === "function") {
      node.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const periodButtons = HISTORY_PERIODS.map((period) => ({
    ...period,
    on: filter.period === period.key,
  }));

  const divisionLabels = (props.divisionOptions ?? [])
    .map((option) => option.label)
    .filter((label) => label !== "");

  const divisionChoices = ["ทั้งหมด", ...divisionLabels];

  /* โครงของแบบ 4d: หัว/picker/นับ/การ์ด เป็น children ของ main ตามลำดับเดียวกับ design */
  return (
    <div className="hv4-ph-main" style={CONTENT_STYLE}>
      <div
        className="hv4-ph-head"
        style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "center", gap: "8px 16px" }}
      >
        <div>
          <h1 className="hv4-ph-h1" style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>
            Patient History Viewer
          </h1>
          <div className="hv4-ph-sub" style={{ fontSize: 14, color: "#5B6C73" }}>
            ประวัติการรักษาทุกครั้ง (อ่านอย่างเดียว)
          </div>
        </div>
      </div>

      {/* encounter picker (compact ตามแบบ 4d) */}
      <div
        className="hv4-ph-picker"
        style={{ display: "flex", flexWrap: "wrap", alignItems: "flex-end", gap: "10px 12px" }}
      >
        <div>
          <div className="hv4-label">Encounter</div>
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <button
              aria-label="Encounter ที่เก่ากว่า"
              title="เก่ากว่า"
              className="hv4-ph-arrow"
              style={ARROW_BUTTON(currentIndex >= 0 && currentIndex < encounters.length - 1)}
              onClick={() => moveEncounter(1)}
            >
              ◀
            </button>
            <select
              className="hv4-input hv4-ph-select"
              aria-label="เลือก Encounter"
              style={{ width: 340, maxWidth: "100%" }}
              value={current === null ? "" : encounterLabel(displayRow(current))}
              onChange={(event) => pickEncounterByLabel(event.target.value)}
            >
              {encounters.map((row) => (
                <option key={row.id}>{encounterLabel(displayRow(row))}</option>
              ))}
            </select>
            <button
              aria-label="Encounter ที่ใหม่กว่า"
              title="ใหม่กว่า"
              className="hv4-ph-arrow"
              style={ARROW_BUTTON(currentIndex > 0)}
              onClick={() => moveEncounter(-1)}
            >
              ▶
            </button>
          </div>
        </div>

        <div>
          <div className="hv4-label">ช่วงเวลา</div>
          <div style={{ display: "flex", gap: 6 }}>
            {periodButtons.map((period) => (
              <button
                key={period.key}
                className="hv4-ph-period"
                aria-pressed={period.on}
                style={PERIOD_BUTTON(period.on)}
                onClick={() => selectPeriod(period.key)}
              >
                {period.label}
              </button>
            ))}
          </div>
        </div>

        <div style={{ flex: "1 1 200px", minWidth: 180 }}>
          <label className="hv4-label" htmlFor="hv4_ph_q">
            ค้นหาชื่อวินิจฉัยหลัก / ยา / แพทย์ (เฉพาะรายการที่โหลดแล้ว)
          </label>
          <input
            id="hv4_ph_q"
            className="hv4-input"
            placeholder="เช่น Vitamin D หรือชื่อแพทย์"
            value={filter.search}
            onChange={(event) => patchFilter({ search: event.target.value })}
          />
        </div>

        <button
          className="hv4-ph-filter-toggle"
          aria-expanded={showFilter}
          style={PLAIN_BUTTON}
          onClick={() => setShowFilter((previous) => !previous)}
        >
          ตัวกรองเพิ่มเติม {showFilter ? "▲" : "▼"}
        </button>
      </div>

      <div className="hv4-ph-count" style={{ fontSize: 13, color: "#51646A" }}>
        พบ {encounters.length} Encounter{periodNote(live ? slice.filter.period : filter.period)}
        {live ? ` · โหลดแล้ว ${allEncounters.length} จาก ${slice.total}` : ""}
        {loading ? " · กำลังโหลด…" : ""}
        {live && slice.nextOffset !== null && slice.nextOffset !== undefined && (
          <button disabled={loading} style={PLAIN_BUTTON} onClick={() => props.runSequence?.({
            sequence: HISTORY_SEQUENCE, action: HISTORY_ACTIONS.LOAD, card: HISTORY_CARD_NAME,
            ...ids, divisionId: props.divisionOptions?.find((option) => option.label === slice.filter.division)?.id,
            filter: slice.filter, offset: slice.nextOffset!,
          })}>โหลดต่อ</button>
        )}
      </div>

      {error !== null && <AlertV4 tone="danger" onClose={dismissError}>{error}</AlertV4>}

      {showFilter && (
        <div className="hv4-ph-filters" style={FILTER_PANEL}>
          <div style={{ flex: "none" }}>
            <div className="hv4-label">ประเภท</div>
            <div style={{ display: "flex" }}>
              <button
                aria-pressed={filter.opd}
                className="hv4-ph-type"
                style={TYPE_BUTTON(filter.opd)}
                onClick={() => patchFilter({ opd: !filter.opd })}
              >
                ผู้ป่วยนอก
              </button>
              <button
                aria-pressed={filter.ipd}
                className="hv4-ph-type"
                style={TYPE_BUTTON(filter.ipd)}
                onClick={() => patchFilter({ ipd: !filter.ipd })}
              >
                ผู้ป่วยใน
              </button>
            </div>
          </div>

          <div style={{ flex: "1 1 150px", minWidth: 150 }}>
            <label className="hv4-label" htmlFor="hv4_ph_from">
              วันที่รับบริการ
            </label>
            <input
              id="hv4_ph_from"
              className="hv4-input"
              placeholder="วว/ดด/ปปปป"
              value={filter.from}
              onChange={(event) => patchFilter({ from: event.target.value })}
            />
            {historyDateError(filter) && <div role="alert" style={{ color: "#8A1C12", fontSize: 13 }}>{historyDateError(filter)}</div>}
          </div>

          <div style={{ flex: "1 1 150px", minWidth: 150 }}>
            <label className="hv4-label" htmlFor="hv4_ph_to">
              ถึง
            </label>
            <input
              id="hv4_ph_to"
              className="hv4-input"
              placeholder="วว/ดด/ปปปป"
              value={filter.to}
              onChange={(event) => patchFilter({ to: event.target.value })}
            />
          </div>

          <div style={{ flex: "1 1 0", minWidth: 96 }}>
            <label className="hv4-label" htmlFor="hv4_ph_div">
              แผนก
            </label>
            <select
              id="hv4_ph_div"
              className="hv4-input"
              value={filter.division}
              onChange={(event) => patchFilter({ division: event.target.value })}
            >
              {divisionChoices.map((label) => (
                <option key={label}>{label}</option>
              ))}
            </select>
          </div>

          <div style={{ flex: "1 1 0", minWidth: 96 }}>
            <label className="hv4-label" htmlFor="hv4_ph_doc">
              แพทย์
            </label>
            <input
              id="hv4_ph_doc"
              className="hv4-input"
              value={filter.doctor}
              onChange={(event) => patchFilter({ doctor: event.target.value })}
            />
          </div>

          <button
            className="hv4-ph-search"
            style={{ ...PLAIN_BUTTON, flex: "none", background: "#1B7FCC", color: "#fff", border: 0, fontWeight: 600 }}
            onClick={() => applySearch()}
          >
            Search
          </button>
          <button
            className="hv4-ph-clear"
            style={{ ...PLAIN_BUTTON, flex: "none", background: "#F28A1E", color: "#fff", border: 0, fontWeight: 600 }}
            onClick={clearFilter}
          >
            เคลียร์
          </button>


        </div>
      )}

      {current !== null && (
        <div className="hv4-ph-visit" style={VISIT_CARD}>
          <div className="hv4-ph-sticky" style={STICKY_HEAD}>
            <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 10 }}>
              <div className="hv4-ph-info" style={{ fontSize: 14, color: "#243036", flex: "1 1 320px", minWidth: 0 }}>
                {encounterInfoText(displayRow(current), divisionFallback)}
              </div>
              <button className="hv4-ph-print" style={PRINT_BUTTON} disabled={(live && (detail === null || !!slice.detailLoading || loading)) || isLocked} onClick={printEmr}>
                🖨 พิมพ์ EMR Summary (PDF)
              </button>
              <button className="hv4-ph-hide" style={HIDE_BUTTON} onClick={toggleHide}>
                {hiddenLabel(current !== null && hiddenIds.includes(current.id))}
              </button>
            </div>

            {nav.length > 0 && !isLocked && (
              <div className="hv4-ph-nav" style={{ marginTop: 8, display: "flex", flexWrap: "wrap", gap: 6 }}>
                {nav.map((item) => (
                  <button
                    key={item.id}
                    className="hv4-ph-nav-pill"
                    style={NAV_PILL}
                    onClick={() => scrollToSection(item.id)}
                  >
                    {item.title}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="hv4-ph-body" style={{ padding: "4px 20px 20px" }}>
            {msg !== "" && (
              <div role="status" style={msgStyle()}>
                {msg}
              </div>
            )}

            {isLocked && <div style={lockedStyle()}>{LOCKED_TEXT}</div>}

            {!isLocked && partialErrors && (
              <div style={{ marginTop: 10, fontSize: 13, color: "#8A1C12" }}>
                อ่านข้อมูลบางส่วนของ Encounter นี้ไม่ครบทุกส่วน:{" "}
                {detail === null ? "" : detail.partialErrors.map(historyPartLabel).join(", ")}
              </div>
            )}

            {!isLocked && detail !== null && (
              <SectionList sections={sections} onPrintFile={printFile} />
            )}

            {!isLocked && live && detail === null && (
              <div style={{ padding: "10px 4px", color: "#5B6C73", fontSize: 14 }}>
                กำลังโหลดรายละเอียดของ Encounter นี้…
              </div>
            )}
          </div>
        </div>
      )}

      {certificate !== null && !isLocked && certificate.historyPatientId === ids.patientId && certificate.historyEncounterId === current?.id && (
        <ModalV4 title="ข้อมูลใบรับรองแพทย์" onClose={() => setCertificate(null)}>
          <div style={{ whiteSpace: "pre-wrap" }}>
            {[certificate.code, certificate.detail, certificate.diagnosis, certificate.treatment].filter(Boolean).join("\n")}
          </div>
          <p>การพิมพ์ต้นฉบับยังต้องใช้เมนูใบรับรองแพทย์เดิม</p>
        </ModalV4>
      )}
      {current === null && (
        <div style={{ padding: 24, border: "1px dashed #CBD3D7", borderRadius: 6, color: "#5B6C73" }}>
          ไม่พบ encounter ที่ตรงกับเงื่อนไข (ปรับช่วงเวลา/ตัวกรอง หรือกด Search ใหม่)
        </div>
      )}
    </div>
  );
};

/* ============================================================== */
/*   SECTION RENDERER (ตามโครง <section> ของแบบ 4d)                */
/* ============================================================== */

const SectionList: React.FC<{
  sections: HistorySection[];
  onPrintFile: (file: HistoryFileRow) => void;
}> = ({ sections, onPrintFile }) => (
  <>
    {sections.map((section) => (
      <section key={section.id} id={section.id} style={{ marginTop: 18, scrollMarginTop: 150 }}>
        <h3 style={SECTION_HEAD}>{section.title}</h3>
        {section.notice && <div role="alert" style={{ color: "#8A1C12" }}>{section.notice}</div>}

        {section.kind === "rows" && (
          <div style={ROW_WRAP}>
            {section.rows.map((row) => (
              <div key={row.key} style={rowStyle(section.grid)}>
                <div style={{ fontSize: 15, fontWeight: 700 }}>{row.key}</div>
                <div style={{ fontSize: 14, ...(row.center ? { textAlign: "center", maxWidth: 250 } : {}) }}>
                  {row.value}
                </div>
              </div>
            ))}
          </div>
        )}

        {section.kind === "table" && section.lines.length === 0 && (
          <div style={{ padding: "6px 4px", color: "#5B6C73", fontSize: 14 }}>{section.emptyText}</div>
        )}

        {section.kind === "table" && section.lines.length > 0 && (
          <div style={{ overflowX: "auto" }}>
            <div style={{ minWidth: 520 }}>
              <div style={tableHeadStyle(section.grid)}>
                {section.columns.map((column) => (
                  <div key={column}>{column}</div>
                ))}
              </div>
              {section.lines.map((line, lineIndex) => (
                <div
                  key={`${section.id}-${lineIndex}`}
                  className="hv4-ph-line"
                  style={gridStyle(section.grid)}
                >
                  {line.cells.map((cell, cellIndex) => (
                    /* cell ที่ 1 ของตารางแลปคือ "ผล" — แบบ 4d ทาสี H/L ของ cell นี้ */
                    <div
                      key={`${section.id}-${lineIndex}-${cellIndex}`}
                      style={cellIndex === 1 && line.flag !== "" ? FLAG_CHIP(line.flag) : undefined}
                    >
                      {cell}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        )}

        {section.kind === "pics" && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
            {section.pictures.map((picture) => (
              <div
                key={picture.id}
                style={{
                  width: 230,
                  border: "1px solid #D5DEE2",
                  borderRadius: 6,
                  overflow: "hidden",
                  background: "#fff",
                }}
              >
                <div
                  style={{
                    height: 110,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 15,
                    color: "#51646A",
                    background: picture.url === "" ? "#E6ECEE" : undefined,
                  }}
                >
                  {picture.url === "" ? picture.mark : null}
                  {picture.url !== "" && (
                    <img src={picture.url} alt={picture.caption} style={{ width: "100%", height: 110, objectFit: "contain" }} />
                  )}
                </div>
                <div style={{ padding: "8px 10px", fontSize: 14, lineHeight: 1.6 }}>
                  <b>{picture.label}</b>
                  <br />
                  {picture.caption}
                  <br />
                  <span style={{ fontSize: 13, color: "#5B6C73" }}>{picture.meta}</span>
                </div>
              </div>
            ))}
          </div>
        )}

        {section.kind === "files" && section.files.length === 0 && !section.notice && <div>{section.emptyText}</div>}
        {section.kind === "files" && (
          <div style={{ border: "1px solid #D5DEE2", borderRadius: 4, overflow: "hidden" }}>
            {section.files.map((file) => (
              <div
                key={file.id}
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "center",
                  gap: "10px 14px",
                  padding: "10px 14px",
                  borderBottom: "1px solid #E3E9EC",
                  background: "#fff",
                }}
              >
                <span
                  aria-hidden="true"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 40,
                    height: 48,
                    borderRadius: 4,
                    background: "#FBE0DD",
                    color: "#8A1C12",
                    fontSize: 12,
                    fontWeight: 700,
                  }}
                >
                  {file.certificate ? "ใบรับรอง" : "รูปภาพ"}
                </span>
                <div style={{ flex: "1 1 260px", minWidth: 0 }}>
                  <div style={{ fontSize: 15, fontWeight: 700 }}>{file.name}</div>
                  <div style={{ fontSize: 13, color: "#5B6C73" }}>{file.meta}</div>
                </div>
                {!file.certificate && safeHistoryUrl(file.url) && <a href={safeHistoryUrl(file.url)} target="_blank" rel="noopener noreferrer">เปิดรูปต้นฉบับ</a>}
                <button
                  className="hv4-ph-reprint"
                  style={{
                    minHeight: 44,
                    padding: "0 16px",
                    border: 0,
                    borderRadius: 4,
                    background: "#1B7FCC",
                    color: "#fff",
                    fontSize: 14,
                    fontWeight: 600,
                  }}
                  onClick={() => onPrintFile(file)}
                >
                  {file.certificate ? "ดูข้อมูลใบรับรอง" : "🖨 พิมพ์รูปต้นฉบับ"}
                </button>
              </div>
            ))}
          </div>
        )}
      </section>
    ))}
  </>
);

export default CardPatientHistoryV4;
